/**
 * Product Search Autocomplete Component
 *
 * Live-filtered search for catalog products with thumbnail display.
 * Supports Magic (mtgjson), Pokémon, and Lorcana (tcgcsv).
 * Filters: Set Code, Card Number, Card Name.
 */
import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { colors, spacing, typography, typographyFlat, borderRadius, shadows, transitions } from '../../styles/tokens';
import { getApiClient } from '../../services/api';
import { CardImage } from '../card/CardImage';

export type CatalogKind = 'cards' | 'tokens' | 'art';
export type CatalogGame = 'mtg' | 'pokemon' | 'lorcana';

export interface Product {
  product_id: string;
  name: string;
  set_name: string;
  set_code?: string;
  card_number?: string;
  rarity?: string;
  catalog?: CatalogKind;
  catalog_source?: 'mtgjson' | 'tcgcsv';
  product_line?: string;
  tcgplayer_id?: string;
  image_url?: string;
  scryfall_id?: string;
  market_price?: number;
  has_foil?: boolean | null;
  has_nonfoil?: boolean | null;
  available_finishes?: Array<'normal' | 'foil' | 'etched' | 'signed' | 'reverse_holofoil'>;
  has_signed?: boolean;
  is_dual_faced?: boolean;
  reverse_product_id?: string;
  reverse_image_url?: string;
  reverse_scryfall_id?: string;
  /** MTGJSON cards.language normalized to copy codes (EN, JA, …). */
  language?: string;
}

export type SearchField = 'cardNumber' | 'cardName';

export interface ProductSearchAutocompleteHandle {
  focusLastSearchField: () => void;
}

interface ProductSearchAutocompleteProps {
  onProductSelect: (product: Product) => void;
  defaultCondition?: string;
  /**
   * Defaults the game tab (`?game=` on Add Inventory).
   * A later change applies a new deep link. Re-rendering the same value
   * does not undo an operator tab switch.
   */
  initialGame?: CatalogGame;
}

export const ProductSearchAutocomplete = forwardRef<
  ProductSearchAutocompleteHandle,
  ProductSearchAutocompleteProps
>(function ProductSearchAutocomplete(
  {
    onProductSelect,
    defaultCondition = 'NM',
    initialGame = 'mtg',
  },
  ref,
) {

  const [setCode, setSetCode] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [catalog, setCatalog] = useState<CatalogKind>('cards');
  const [game, setGame] = useState<CatalogGame>(initialGame);
  const appliedInitialGameRef = useRef(initialGame);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [persistSetCode, setPersistSetCode] = useState(false);
  const [flippedProducts, setFlippedProducts] = useState<Set<string>>(new Set());
  
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoSelectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const suppressSearchRef = useRef(false);
  const lastSetCodeRef = useRef('');
  const lastSearchFieldRef = useRef<SearchField>('cardNumber');
  const cardNumberInputRef = useRef<HTMLInputElement>(null);
  const cardNameInputRef = useRef<HTMLInputElement>(null);
  const searchSeqRef = useRef(0);

  const focusLastSearchField = useCallback(() => {
    const target =
      lastSearchFieldRef.current === 'cardName'
        ? cardNameInputRef.current
        : cardNumberInputRef.current;
    (target ?? cardNumberInputRef.current ?? cardNameInputRef.current)?.focus();
  }, []);

  useImperativeHandle(ref, () => ({
    focusLastSearchField,
  }), [focusLastSearchField]);

  // Debounced search function
  const searchProducts = useCallback(async (
    set: string,
    number: string,
    name: string,
    catalogKind: CatalogKind,
    catalogGame: CatalogGame,
  ) => {
    // Don't search if all fields are empty
    if (!set && !number && !name) {
      searchSeqRef.current += 1;
      setProducts([]);
      setLoading(false);
      setError(null);
      return;
    }

    const requestId = ++searchSeqRef.current;
    setLoading(true);
    setError(null);

    try {
      const params: Record<string, string> = {
        limit: '100',
        include_image_url: 'true',
        catalog: catalogGame === 'mtg' ? catalogKind : 'cards',
        game: catalogGame,
      };

      if (set) params.set_code = set;
      if (number) params.card_number = number;
      if (name) params.product_name = name;

      const apiClient = getApiClient();
      const response = await apiClient.get('/products', { params });

      // Ignore stale responses from earlier keystrokes.
      if (requestId !== searchSeqRef.current) {
        return;
      }

      const warning = response.data?.warning;
      if (warning) {
        setError(String(warning));
        setProducts([]);
        return;
      }

      setProducts(response.data.products || []);
    } catch (err: any) {
      if (requestId !== searchSeqRef.current) {
        return;
      }
      console.error('Product search error:', err);
      setError(err.message || 'Failed to search products');
      setProducts([]);
    } finally {
      if (requestId === searchSeqRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Handle product selection from autocomplete
  const handleProductClick = useCallback((product: Product) => {
    const isFlipped = flippedProducts.has(product.product_id);
    const selectedProduct = isFlipped && product.reverse_product_id
      ? {
          ...product,
          product_id: product.reverse_product_id,
          reverse_product_id: product.product_id,
          scryfall_id: product.reverse_scryfall_id ?? product.scryfall_id,
          reverse_scryfall_id: product.scryfall_id ?? product.reverse_scryfall_id,
          image_url: product.reverse_image_url ?? product.image_url,
          reverse_image_url: product.image_url ?? product.reverse_image_url,
        }
      : product;
    
    onProductSelect(selectedProduct);
    // Clear search inputs after selection, but keep set code if persisting
    suppressSearchRef.current = true;
    if (!persistSetCode) {
      setSetCode('');
    }
    setCardNumber('');
    setCardName('');
    setProducts([]);
    setFlippedProducts(new Set()); // Clear flip state
    
    // Return focus to the field used to find this card
    setTimeout(() => {
      focusLastSearchField();
    }, 0);
  }, [onProductSelect, persistSetCode, flippedProducts, focusLastSearchField]);

  // Handle flipping dual-faced cards
  const handleFlipCard = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, product: Product) => {
      e.stopPropagation(); // Don't select product when flipping
      
      if (!product.is_dual_faced || !product.reverse_product_id) {
        return;
      }

      setFlippedProducts((prev) => {
        const newFlipped = new Set(prev);
        if (newFlipped.has(product.product_id)) {
          newFlipped.delete(product.product_id);
        } else {
          newFlipped.add(product.product_id);
        }
        return newFlipped;
      });
    },
    []
  );

  // Get the currently displayed product (may be flipped)
  const getDisplayedProduct = useCallback(
    (product: Product): Product => {
      if (
        product.is_dual_faced &&
        product.reverse_product_id &&
        flippedProducts.has(product.product_id)
      ) {
        // Return the reverse side info with its image
        return {
          ...product,
          product_id: product.reverse_product_id,
          image_url: product.reverse_image_url || product.image_url, // Show reverse image
          reverse_image_url: product.image_url, // Swap back: reverse side now shows the front image
          is_dual_faced: true,
          reverse_product_id: product.product_id,
        };
      }
      return product;
    },
    [flippedProducts]
  );


  // Debounce search on input changes
  useEffect(() => {
    if (setCode !== lastSetCodeRef.current) {
      suppressSearchRef.current = false;
      lastSetCodeRef.current = setCode;
    }

    if (cardNumber || cardName) {
      suppressSearchRef.current = false;
    }

    if (suppressSearchRef.current && !cardNumber && !cardName) {
      setProducts([]);
      setLoading(false);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      searchProducts(setCode, cardNumber, cardName, catalog, game);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [setCode, cardNumber, cardName, catalog, game, searchProducts]);

  // Card set codes often have no token/art rows (e.g. LEA). Drop the pinned set
  // when switching catalogs so a leftover filter does not look like "no results".
  const handleGameSelect = useCallback((next: CatalogGame) => {
    if (next === game) {
      return;
    }
    suppressSearchRef.current = false;
    lastSetCodeRef.current = '';
    setGame(next);
    if (next !== 'mtg') {
      setCatalog('cards');
    }
    setSetCode('');
    setProducts([]);
    setError(null);
  }, [game]);

  // Deep link defaults the tab. The same initialGame on later renders must
  // not snap the operator back after they switch games mid-batch.
  useEffect(() => {
    if (initialGame === appliedInitialGameRef.current) {
      return;
    }
    appliedInitialGameRef.current = initialGame;
    handleGameSelect(initialGame);
  }, [initialGame, handleGameSelect]);

  const handleCatalogSelect = useCallback((next: CatalogKind) => {
    if (next === catalog) {
      return;
    }
    suppressSearchRef.current = false;
    lastSetCodeRef.current = '';
    setCatalog(next);
    setSetCode('');
    setProducts([]);
    setError(null);
  }, [catalog]);

  // Auto-select only for collector-number lookups (set + number / number alone).
  // Name search must never auto-select: narrowing to one match mid-typing clears the
  // inputs and looks like search "found nothing" — especially with a pinned set code.
  useEffect(() => {
    if (autoSelectTimerRef.current) {
      clearTimeout(autoSelectTimerRef.current);
    }

    const canAutoSelect = Boolean(cardNumber.trim()) && !cardName.trim();

    if (canAutoSelect && products.length === 1 && !loading) {
      autoSelectTimerRef.current = setTimeout(() => {
        handleProductClick(products[0]);
      }, 500);
    }

    return () => {
      if (autoSelectTimerRef.current) {
        clearTimeout(autoSelectTimerRef.current);
      }
    };
  }, [products, loading, handleProductClick, cardNumber, cardName]);

  const catalogLabel =
    catalog === 'tokens' ? 'tokens' : catalog === 'art' ? 'art cards' : 'products';
  const setPlaceholder =
    game === 'pokemon'
      ? 'e.g., PBL, ME05'
      : game === 'lorcana'
        ? 'e.g., TFC, URS'
        : catalog === 'tokens'
          ? 'e.g., MH3, TMH3'
          : catalog === 'art'
            ? 'e.g., MH2, AMH2'
            : 'e.g., LEA, M21';

  return (
    <div style={styles.container} data-default-condition={defaultCondition}>
      <div style={styles.sectionHeader}>
        <h3 style={styles.sectionTitle}>Search Products</h3>
        <div style={styles.headerToggles}>
          <div style={styles.catalogToggle} role="tablist" aria-label="Game">
            {([
              { id: 'mtg', label: 'MTG' },
              { id: 'pokemon', label: 'Pokémon' },
              { id: 'lorcana', label: 'Lorcana' },
            ] as const).map((option) => {
              const active = game === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => handleGameSelect(option.id)}
                  style={{
                    ...styles.catalogButton,
                    ...(active ? styles.catalogButtonActive : {}),
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          {game === 'mtg' && (
            <div style={styles.catalogToggle} role="tablist" aria-label="Catalog">
              {([
                { id: 'cards', label: 'Cards' },
                { id: 'tokens', label: 'Tokens' },
                { id: 'art', label: 'Art Cards' },
              ] as const).map((option) => {
                const active = catalog === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => handleCatalogSelect(option.id)}
                    style={{
                      ...styles.catalogButton,
                      ...(active ? styles.catalogButtonActive : {}),
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      {/* Search Inputs */}
      <div style={styles.searchGrid}>
        <div style={styles.field}>
          <div style={styles.fieldHeader}>
            <label style={styles.label} htmlFor="setCode">Set Code</label>
            <button
              type="button"
              onClick={() => setPersistSetCode(!persistSetCode)}
              title={persistSetCode ? 'Unpin set code' : 'Pin set code for multiple cards'}
              style={{
                ...styles.persistButton,
                ...(persistSetCode ? styles.persistButtonActive : {}),
              }}
            >
              {persistSetCode ? 'Pinned' : 'Pin'}
            </button>
          </div>
          <input
            id="setCode"
            type="text"
            placeholder={setPlaceholder}
            value={setCode}
            onChange={(e) => setSetCode(e.target.value.toUpperCase())}
            style={styles.input}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label} htmlFor="cardNumber">Card Number</label>
          <input
            ref={cardNumberInputRef}
            id="cardNumber"
            type="text"
            placeholder="e.g., 1, 234"
            value={cardNumber}
            onFocus={() => {
              lastSearchFieldRef.current = 'cardNumber';
            }}
            onChange={(e) => {
              lastSearchFieldRef.current = 'cardNumber';
              setCardNumber(e.target.value);
            }}
            style={styles.input}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label} htmlFor="cardName">Card Name</label>
          <input
            ref={cardNameInputRef}
            id="cardName"
            type="text"
            placeholder="e.g., Black Lotus"
            value={cardName}
            onFocus={() => {
              lastSearchFieldRef.current = 'cardName';
            }}
            onChange={(e) => {
              lastSearchFieldRef.current = 'cardName';
              setCardName(e.target.value);
            }}
            style={styles.input}
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={styles.statusMessage}>
          Searching...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={styles.errorMessage}>
          {error}
        </div>
      )}

      {/* Results Grid */}
      {!loading && !error && products.length > 0 && (
        <div style={styles.resultsContainer}>
          <div style={styles.resultsGrid}>
            {products.map((product) => {
              const displayedProduct = getDisplayedProduct(product);
              const isFlipped = flippedProducts.has(product.product_id);
              
              return (
                <div
                  key={product.product_id}
                  style={styles.productCard}
                  onClick={() => handleProductClick(product)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleProductClick(product);
                    }
                  }}
                >
                  {/* Card Image with Flip Support */}
                  <div style={{ ...styles.thumbnailContainer, position: 'relative' }}>
                    <CardImage
                      imageUrl={product.image_url}
                      scryfallId={product.scryfall_id}
                      reverseImageUrl={product.reverse_image_url}
                      reverseScryfallId={product.reverse_scryfall_id}
                      isDualFaced={product.is_dual_faced}
                      isFlipped={isFlipped}
                      cardName={product.name}
                      size="fill"
                      onFlip={(e) => handleFlipCard(e, product)}
                    />
                  </div>

                  {/* Card Details */}
                  <div style={styles.cardDetails}>
                    <div style={styles.cardName}>{displayedProduct.name}</div>
                    {isFlipped && product.is_dual_faced && (
                      <div style={{ 
                        fontSize: typography.caption.fontSize, 
                        color: colors.info,
                        fontWeight: 'bold',
                        marginBottom: spacing.xs 
                      }}>
                        Reverse &rarr;
                      </div>
                    )}
                    <div style={styles.cardMeta}>
                      {displayedProduct.set_code && <span style={styles.setCode}>{displayedProduct.set_code}</span>}
                      {displayedProduct.card_number && <span style={styles.cardNumber}>#{displayedProduct.card_number}</span>}
                    </div>
                    <div style={styles.setName}>{displayedProduct.set_name}</div>
                    {displayedProduct.rarity && (
                      <div style={styles.rarity}>
                        <span style={getRarityStyle(displayedProduct.rarity)}>{displayedProduct.rarity}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No Results — only after number/name input. Pinned set-alone after a select is not a failed search. */}
      {!loading && !error && products.length === 0 && (cardNumber.trim() || cardName.trim()) && (
        <div style={styles.statusMessage}>
          No {catalogLabel} found. Try adjusting your search.
        </div>
      )}
    </div>
  );
});

// Helper function for rarity colors
const getRarityStyle = (rarity: string): React.CSSProperties => {
  const rarityColors: Record<string, string> = {
    'mythic': '#d4af37',
    'rare': '#ffd700',
    'uncommon': '#c0c0c0',
    'common': '#000000',
  };

  const color = rarityColors[rarity.toLowerCase()] || colors.textSecondary;

  return {
    fontSize: typography.caption.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: color,
    textTransform: 'uppercase',
  };
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.md,
  },
  sectionTitle: {
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
    margin: 0,
  },
  sectionHeader: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  headerToggles: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    gap: spacing.sm,
  },
  catalogToggle: {
    display: 'inline-flex',
    flexWrap: 'wrap' as const,
    gap: spacing.xs,
    borderRadius: borderRadius.full,
    backgroundColor: colors.gray100,
    padding: spacing.xs,
  },
  catalogButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${spacing.xs} ${spacing.md}`,
    border: 'none',
    borderRadius: borderRadius.full,
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.semibold,
    lineHeight: '1.2',
    cursor: 'pointer',
    transition: `all ${transitions.base}`,
  },
  catalogButtonActive: {
    color: colors.primaryDark,
    backgroundColor: colors.white,
    boxShadow: `inset 0 0 0 1px ${colors.border}`,
  },
  searchGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.md,
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
  },
  fieldHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.text,
  },
  persistButton: {
    fontSize: typography.caption.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    padding: `${spacing.xs} ${spacing.sm}`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.background,
    color: colors.textSecondary,
    cursor: 'pointer',
    opacity: 0.8,
    transition: `all ${transitions.base}`,
  },
  persistButtonActive: {
    opacity: 1,
    borderColor: colors.primary,
    color: colors.primary,
  },
  input: {
    fontSize: typography.body.fontSize,
    padding: `${spacing.sm} ${spacing.md}`,
    border: `2px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    color: colors.text,
    outline: 'none',
    transition: `border-color ${transitions.base} ease-in-out`,
    height: spacing.xl,
  },
  statusMessage: {
    padding: spacing.md,
    textAlign: 'center' as const,
    fontSize: typography.body.fontSize,
    color: colors.textSecondary,
  },
  errorMessage: {
    padding: spacing.md,
    backgroundColor: '#fee',
    border: `1px solid ${colors.error}`,
    borderRadius: borderRadius.md,
    color: colors.error,
    fontSize: typography.small.fontSize,
  },
  resultsContainer: {
    marginTop: spacing.md,
  },
  resultsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: spacing.md,
    maxHeight: '500px',
    overflowY: 'auto' as const,
    padding: spacing.sm,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  },
  productCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    cursor: 'pointer',
    padding: spacing.sm,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    transition: `all ${transitions.base}`,
    ':hover': {
      borderColor: colors.primary,
      boxShadow: shadows.md,
      transform: 'translateY(-2px)',
    },
  },
  thumbnailContainer: {
    width: '100%',
    aspectRatio: '5/7',
    overflow: 'hidden',
    borderRadius: borderRadius.sm,
    marginBottom: spacing.xs,
  },
  thumbnail: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  },
  placeholderThumbnail: {
    width: '100%',
    height: '100%',
    backgroundColor: '#333',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  placeholderText: {
    fontSize: typography.caption.fontSize,
    color: '#999',
  },
  cardDetails: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
  },
  cardName: {
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
    lineHeight: '1.2',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as any,
  },
  cardMeta: {
    display: 'flex',
    gap: spacing.xs,
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
  },
  setCode: {
    fontWeight: typographyFlat.fontWeight.bold,
  },
  cardNumber: {
    color: colors.textSecondary,
  },
  setName: {
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  rarity: {
    marginTop: spacing.xs,
  },
};
